import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Loader2, Upload, FileText, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trackEvent } from '@/utils/analytics';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useFormAutosave } from '@/hooks/useFormAutosave';
import { reportFailedSubmission } from '@/hooks/useFormFailureBeacon';
import { RestoreDraftBanner } from '@/components/shared/RestoreDraftBanner';
import { getAiSessionId } from '@/utils/aiAttribution';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOTAL_STEPS = 4;

const PROJECT_TYPES = ['New Construction', 'Renovation', 'Replacement', 'Not sure'];
const FACILITY_TYPES = ['Retail', 'Healthcare', 'Education', 'Corporate / Office', 'Industrial', 'Hospitality', 'Other'];
const PROJECT_SIZES = ['Under 1,000 sq ft', '1,000–5,000 sq ft', '5,000–20,000 sq ft', '20,000+ sq ft'];
const DESIGN_GOALS = ['Branding / logo integration', 'Durability / high traffic', 'Safety / slip resistance', 'Aesthetic upgrade', 'Budget optimization', 'Sustainability', 'Not sure / need guidance'];
const PRODUCT_INTERESTS = ['Custom Logo Matting', 'Broadloom Installation', 'Luxury Vinyl Plank & Tile', 'Commercial Mat Roll-out', 'Rubber Flooring', 'Not sure'];
const TIMELINES = ['ASAP', '1–4 weeks', '3–6 months', '6+ months'];
const BUDGETS = ['Under $10K', '$10K–$50K', '$50K–$150K', '$150K+', 'Not sure'];
const CONTACT_PREFS = ['Phone', 'Email', 'Schedule a call'];

const RadioGroup = ({ options, value, onChange, name, testPrefix }) => (
  <div className="grid grid-cols-2 gap-2">
    {options.map(opt => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        data-testid={`${testPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
        className={`px-3 py-2.5 text-sm rounded-lg border text-left transition-all ${
          value === opt
            ? 'border-primary bg-primary/5 text-primary font-medium'
            : 'border-gray-200 text-gray-600 hover:border-gray-300'
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

const MultiSelect = ({ options, selected, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(opt => {
      const isSelected = selected.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(isSelected ? selected.filter(s => s !== opt) : [...selected, opt])}
          data-testid={`${testPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
          className={`px-3 py-2 text-sm rounded-full border transition-all ${
            isSelected
              ? 'border-primary bg-primary text-white'
              : 'border-gray-200 text-gray-600 hover:border-gray-300'
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const ConsultationIntakeModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [files, setFiles] = useState([]);

  // Mirror of FastQuoteModal — see those constants for context.
  const MAX_FILES = 10;
  const MAX_FILE_SIZE = 25 * 1024 * 1024;
  const MAX_TOTAL_SIZE = 100 * 1024 * 1024;

  const [form, setForm] = useState({
    fullName: '', email: '', company: '', phone: '',
    projectType: '', facilityType: '', projectSize: '',
    designGoals: [], productInterest: [],
    timeline: '', budget: '',
    projectDetails: '', contactPreference: ''
  });

  const {
    hasDraft, draftAge, restoreDraft, clearDraft, dismissDraftBanner,
  } = useFormAutosave('consultation-intake', form, setForm, { enabled: !isSuccess });

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    const seen = new Set(files.map(f => `${f.name}|${f.size}|${f.lastModified}`));
    const next = [...files];
    for (const f of picked) {
      const key = `${f.name}|${f.size}|${f.lastModified}`;
      if (!seen.has(key)) {
        next.push(f);
        seen.add(key);
      }
    }
    if (next.length > MAX_FILES) {
      setErrors(prev => ({ ...prev, file: `Max ${MAX_FILES} files per submission` }));
      e.target.value = '';
      return;
    }
    for (const f of next) {
      if (f.size > MAX_FILE_SIZE) {
        setErrors(prev => ({ ...prev, file: `“${f.name}” is over 25 MB — please upload smaller` }));
        e.target.value = '';
        return;
      }
    }
    const total = next.reduce((s, f) => s + f.size, 0);
    if (total > MAX_TOTAL_SIZE) {
      setErrors(prev => ({ ...prev, file: `Combined attachments are over 100 MB — please remove one` }));
      e.target.value = '';
      return;
    }
    setFiles(next);
    setErrors(prev => ({ ...prev, file: undefined }));
    e.target.value = '';
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.fullName.trim()) e.fullName = 'Required';
      if (!form.email.trim()) e.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
      if (!form.company.trim()) e.company = 'Required';
    }
    if (s === 2) {
      if (!form.projectType) e.projectType = 'Required';
      if (!form.facilityType) e.facilityType = 'Required';
      if (!form.projectSize) e.projectSize = 'Required';
    }
    if (s === 4) {
      if (!form.projectDetails.trim()) e.projectDetails = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) setStep(s => Math.min(s + 1, TOTAL_STEPS));
  };

  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    setIsSubmitting(true);
    try {
      const body = new FormData();
      body.append('fullName', form.fullName);
      body.append('email', form.email);
      body.append('company', form.company);
      body.append('phone', form.phone);
      body.append('projectType', form.projectType);
      body.append('facilityType', form.facilityType);
      body.append('projectSize', form.projectSize);
      body.append('designGoals', form.designGoals.join(', '));
      body.append('productInterest', form.productInterest.join(', '));
      body.append('timeline', form.timeline);
      body.append('budget', form.budget);
      body.append('projectDetails', form.projectDetails);
      body.append('contactPreference', form.contactPreference);
      files.forEach(f => body.append('files', f));
      const sid = getAiSessionId();
      if (sid) body.append('ai_session_id', sid);

      const res = await fetch(`/api/consultation-intake`, { method: 'POST', body });
      if (!res.ok) throw new Error('Failed');
      trackEvent('consultation_booked', {
        form_type: 'consultation_intake',
        project_type: form.projectType || 'unspecified',
        facility_type: form.facilityType || 'unspecified',
      });
      clearDraft();
      setIsSuccess(true);
    } catch (err) {
      reportFailedSubmission({
        formType: 'consultation_intake',
        draft: form,
        errorReason: err?.message || 'fetch_failed',
      });
      setErrors(prev => ({ ...prev, submit: 'Something went wrong. Please try again, or email sales@porticosystems.com.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep(1);
      setForm({ fullName: '', email: '', company: '', phone: '', projectType: '', facilityType: '', projectSize: '', designGoals: [], productInterest: [], timeline: '', budget: '', projectDetails: '', contactPreference: '' });
      setFiles([]);
      setErrors({});
      setIsSuccess(false);
    }, 300);
  };

  if (!isOpen) return null;

  const stepLabels = ['Contact', 'Project', 'Preferences', 'Details'];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && handleClose()}
        data-testid="intake-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
          data-testid="intake-modal"
        >
          <button onClick={handleClose} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" data-testid="intake-modal-close" aria-label="Close">
            <X className="w-5 h-5 text-gray-600" />
          </button>

          {!isSuccess ? (
            <div className="p-6">
              {/* Header */}
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-1 pr-10">Start Your Design Consultation</h2>
              <p className="text-sm text-gray-500 mb-5">This takes 1–2 minutes and helps us prepare a tailored consultation for your project.</p>

              {/* Step Indicator */}
              <div className="flex items-center gap-1 mb-6">
                {stepLabels.map((label, i) => (
                  <div key={label} className="flex-1">
                    <div className={`h-1.5 rounded-full transition-colors ${i + 1 <= step ? 'bg-primary' : 'bg-gray-200'}`} />
                    <p className={`text-[10px] mt-1 text-center ${i + 1 <= step ? 'text-primary font-medium' : 'text-gray-400'}`}>{label}</p>
                  </div>
                ))}
              </div>

              <RestoreDraftBanner
                show={hasDraft}
                draftAge={draftAge}
                onRestore={restoreDraft}
                onDismiss={dismissDraftBanner}
                testId="consultation-restore-draft"
              />

              {/* Step 1: Contact Info */}
              {step === 1 && (
                <div className="space-y-4" data-testid="intake-step-1">
                  <h3 className="font-heading font-semibold text-sm text-gray-700 uppercase tracking-wider">Contact Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ci-name">Full Name *</Label>
                      <Input id="ci-name" value={form.fullName} onChange={e => set('fullName', e.target.value)} placeholder="John Smith" data-testid="ci-fullname" className={errors.fullName ? 'border-red-400' : ''} />
                      {errors.fullName && <p className="text-xs text-red-500 mt-1">{errors.fullName}</p>}
                    </div>
                    <div>
                      <Label htmlFor="ci-email">Email *</Label>
                      <Input id="ci-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="john@company.com" data-testid="ci-email" className={errors.email ? 'border-red-400' : ''} />
                      {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ci-company">Company Name *</Label>
                      <Input id="ci-company" value={form.company} onChange={e => set('company', e.target.value)} placeholder="Acme Corp" data-testid="ci-company" className={errors.company ? 'border-red-400' : ''} />
                      {errors.company && <p className="text-xs text-red-500 mt-1">{errors.company}</p>}
                    </div>
                    <div>
                      <Label htmlFor="ci-phone">Phone Number</Label>
                      <Input id="ci-phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 123-4567" data-testid="ci-phone" />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Project Overview */}
              {step === 2 && (
                <div className="space-y-5" data-testid="intake-step-2">
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-gray-700 uppercase tracking-wider mb-3">Project Type *</h3>
                    <RadioGroup options={PROJECT_TYPES} value={form.projectType} onChange={v => set('projectType', v)} name="projectType" testPrefix="ci-pt" />
                    {errors.projectType && <p className="text-xs text-red-500 mt-1">{errors.projectType}</p>}
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-gray-700 uppercase tracking-wider mb-3">Facility Type *</h3>
                    <RadioGroup options={FACILITY_TYPES} value={form.facilityType} onChange={v => set('facilityType', v)} name="facilityType" testPrefix="ci-ft" />
                    {errors.facilityType && <p className="text-xs text-red-500 mt-1">{errors.facilityType}</p>}
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-gray-700 uppercase tracking-wider mb-3">Project Size *</h3>
                    <RadioGroup options={PROJECT_SIZES} value={form.projectSize} onChange={v => set('projectSize', v)} name="projectSize" testPrefix="ci-ps" />
                    {errors.projectSize && <p className="text-xs text-red-500 mt-1">{errors.projectSize}</p>}
                  </div>
                </div>
              )}

              {/* Step 3: Preferences */}
              {step === 3 && (
                <div className="space-y-5" data-testid="intake-step-3">
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-gray-700 uppercase tracking-wider mb-1">Design Goals</h3>
                    <p className="text-xs text-gray-400 mb-3">What are your goals for this project? Select all that apply.</p>
                    <MultiSelect options={DESIGN_GOALS} selected={form.designGoals} onChange={v => set('designGoals', v)} testPrefix="ci-dg" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-gray-700 uppercase tracking-wider mb-1">Product Interest</h3>
                    <p className="text-xs text-gray-400 mb-3">Which products are you interested in? Select all that apply.</p>
                    <MultiSelect options={PRODUCT_INTERESTS} selected={form.productInterest} onChange={v => set('productInterest', v)} testPrefix="ci-pi" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-heading font-semibold text-sm text-gray-700 uppercase tracking-wider mb-3">Timeline</h3>
                      <RadioGroup options={TIMELINES} value={form.timeline} onChange={v => set('timeline', v)} name="timeline" testPrefix="ci-tl" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-sm text-gray-700 uppercase tracking-wider mb-3">Budget</h3>
                      <RadioGroup options={BUDGETS} value={form.budget} onChange={v => set('budget', v)} name="budget" testPrefix="ci-bg" />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Details & Upload */}
              {step === 4 && (
                <div className="space-y-4" data-testid="intake-step-4">
                  <div>
                    <Label htmlFor="ci-details">Project Details *</Label>
                    <p className="text-xs text-gray-400 mb-2">Tell us about your space, goals, or any challenges you're trying to solve.</p>
                    <Textarea id="ci-details" value={form.projectDetails} onChange={e => set('projectDetails', e.target.value)} rows={4} placeholder="Describe your project..." data-testid="ci-details" className={errors.projectDetails ? 'border-red-400' : ''} />
                    {errors.projectDetails && <p className="text-xs text-red-500 mt-1">{errors.projectDetails}</p>}
                  </div>

                  <div>
                    <Label>Attach Files</Label>
                    <p className="text-xs text-gray-400 mb-2">
                      Floor plans, drawings, inspiration images, or specs. Up to {MAX_FILES} files · 25 MB each · 100 MB total.
                    </p>
                    <label
                      htmlFor="ci-file"
                      className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors"
                      data-testid="ci-file-upload"
                    >
                      <Upload className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">
                        {files.length === 0 ? "Choose files or drag them here" : "Add more files"}
                      </span>
                      <input
                        id="ci-file"
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                        accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.xlsx,.docx"
                      />
                    </label>
                    {files.length > 0 && (
                      <ul className="mt-2 space-y-1.5" data-testid="ci-file-list">
                        {files.map((f, i) => (
                          <li
                            key={`${f.name}-${f.size}-${f.lastModified}`}
                            className="flex items-center gap-3 px-3 py-2 bg-primary/5 border border-primary/20 rounded-lg"
                          >
                            <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                              <p className="text-xs text-gray-400">
                                {f.size < 1024 * 1024
                                  ? `${Math.round(f.size / 1024)} KB`
                                  : `${(f.size / (1024 * 1024)).toFixed(1)} MB`}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(i)}
                              className="p-1 rounded hover:bg-gray-200 transition-colors"
                              data-testid={`ci-file-remove-${i}`}
                              aria-label={`Remove ${f.name}`}
                            >
                              <X className="w-4 h-4 text-gray-500" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {errors.file && <p className="text-xs text-red-500 mt-1">{errors.file}</p>}
                  </div>

                  <div>
                    <h3 className="font-heading font-semibold text-sm text-gray-700 uppercase tracking-wider mb-3">Preferred Contact Method</h3>
                    <RadioGroup options={CONTACT_PREFS} value={form.contactPreference} onChange={v => set('contactPreference', v)} name="contactPref" testPrefix="ci-cp" />
                  </div>

                  {errors.submit && (
                    <p className="text-sm text-red-500 bg-red-50 p-3 rounded-lg" data-testid="ci-error">{errors.submit}</p>
                  )}
                </div>
              )}

              {/* Navigation */}
              <div className="mt-6 pt-4 border-t border-gray-100 flex gap-3">
                {step > 1 && (
                  <Button variant="outline" onClick={prevStep} className="flex-1" data-testid="ci-back-btn">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                )}
                {step < TOTAL_STEPS ? (
                  <Button onClick={nextStep} className="flex-1" data-testid="ci-next-btn">
                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={isSubmitting} className="flex-1" data-testid="ci-submit-btn">
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {isSubmitting ? 'Submitting...' : 'Submit Consultation Request'}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="p-6 text-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">Consultation Request Received!</h3>
              <p className="text-gray-600 mb-2">Thank you for sharing your project details.</p>
              <p className="text-sm text-gray-400 mb-6">We'll review your details and follow up to schedule your consultation.</p>
              <Button onClick={handleClose} className="w-full" data-testid="ci-done-btn">Done</Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ConsultationIntakeModal;
